import * as Cesium from 'cesium';
import type { SkyObserver } from './SkyCamera';
import {
  CARDINAL_DIRECTIONS,
  HORIZON_LABEL_FONT,
  HORIZON_LABEL_OFFSET_M,
  HORIZON_RING_WIDTH_PX,
  ZENITH_RETICLE_ALTITUDE_M,
  ZENITH_RETICLE_OUTLINE_PX,
  ZENITH_RETICLE_PIXEL_SIZE,
  cardinalEnuOffset,
  horizonRingEnuPoints,
  type CardinalDirection,
} from './skyHorizonConstants';

/**
 * Phase 7 chunk 2B — world-space horizon labels + zenith reticle for
 * sky view.
 *
 * Four text labels (N / E / S / W) are anchored at the observer's
 * local horizon, 1 km out in each cardinal direction at the observer's
 * altitude. From the stargazer's pose (pitch +75°, looking up) these
 * sit slightly below the frustum center — providing legible orientation
 * cues without dominating the view.
 *
 * One faint accent-color zenith reticle is anchored directly above the
 * observer at 5 km altitude. Cesium PointPrimitive's `pixelSize` keeps
 * the reticle screen-stable regardless of distance.
 *
 * Lifecycle:
 *  - `attach(viewer, observer)` builds the 4 labels + reticle, returns
 *    once they're in the scene.
 *  - `detach()` removes them. Idempotent — safe to call without an
 *    active attach.
 *
 * The layer is owned by {@link Globe}; `enterSkyView` calls `attach`
 * and `exitSkyView` calls `detach` so the cardinal markers only exist
 * while sky view is active.
 */
export class SkyHorizonLayer {
  private viewer: Cesium.Viewer | null = null;
  private labelCollection: Cesium.LabelCollection | null = null;
  private pointCollection: Cesium.PointPrimitiveCollection | null = null;
  /** Chunk 2D — ground-level horizon ring polyline. Sits 1 m above
   *  the observer's ground level, 1 km radius, accent-cyan half-alpha. */
  private ringCollection: Cesium.PolylineCollection | null = null;

  /** Build and attach the labels + zenith reticle. */
  attach(viewer: Cesium.Viewer, observer: SkyObserver): void {
    if (this.viewer !== null) return; // already attached
    this.viewer = viewer;

    const observerCart = Cesium.Cartesian3.fromDegrees(
      observer.longitude,
      observer.latitude,
      observer.altitudeMeters ?? 0,
    );
    const enuFrame = Cesium.Transforms.eastNorthUpToFixedFrame(observerCart);

    // ─── Cardinal labels ────────────────────────────────────────────────
    const labels = viewer.scene.primitives.add(
      new Cesium.LabelCollection(),
    ) as Cesium.LabelCollection;
    this.labelCollection = labels;

    for (const dir of CARDINAL_DIRECTIONS) {
      labels.add({
        position: this.cardinalWorldPosition(enuFrame, dir),
        text: dir,
        font: HORIZON_LABEL_FONT,
        fillColor: Cesium.Color.fromCssColorString('#00d9ff').withAlpha(0.85), // accent cyan
        outlineColor: Cesium.Color.BLACK.withAlpha(0.6),
        outlineWidth: 2,
        style: Cesium.LabelStyle.FILL_AND_OUTLINE,
        verticalOrigin: Cesium.VerticalOrigin.CENTER,
        horizontalOrigin: Cesium.HorizontalOrigin.CENTER,
      });
    }

    // ─── Zenith reticle ─────────────────────────────────────────────────
    const points = viewer.scene.primitives.add(
      new Cesium.PointPrimitiveCollection(),
    ) as Cesium.PointPrimitiveCollection;
    this.pointCollection = points;

    points.add({
      position: Cesium.Cartesian3.fromDegrees(
        observer.longitude,
        observer.latitude,
        (observer.altitudeMeters ?? 0) + ZENITH_RETICLE_ALTITUDE_M,
      ),
      pixelSize: ZENITH_RETICLE_PIXEL_SIZE,
      color: Cesium.Color.TRANSPARENT,                                    // ring only
      outlineColor: Cesium.Color.fromCssColorString('#00d9ff').withAlpha(0.5),
      outlineWidth: ZENITH_RETICLE_OUTLINE_PX,
    });

    // ─── Ground-level horizon ring (chunk 2D) ────────────────────────────
    // Marks the observer's local ground plane so the user can see where
    // the imagery-painted Earth ends and the sky begins, especially when
    // dragging the view down past horizontal. Polyline of 64 segments in
    // the observer's local ENU frame, transformed via the same matrix
    // as the cardinal labels.
    const ringCollection = viewer.scene.primitives.add(
      new Cesium.PolylineCollection(),
    ) as Cesium.PolylineCollection;
    this.ringCollection = ringCollection;
    const ringPoints = horizonRingEnuPoints().map((p) => {
      const enu = new Cesium.Cartesian3(p.east, p.north, p.up);
      return Cesium.Matrix4.multiplyByPoint(enuFrame, enu, new Cesium.Cartesian3());
    });
    ringCollection.add({
      positions: ringPoints,
      width: HORIZON_RING_WIDTH_PX,
      material: Cesium.Material.fromType(Cesium.Material.ColorType, {
        color: Cesium.Color.fromCssColorString('#00d9ff').withAlpha(0.5),
      }),
    });
  }

  /** Remove the labels + reticle + ground ring. Idempotent. */
  detach(): void {
    if (this.viewer === null) return;
    if (!this.viewer.isDestroyed()) {
      if (this.labelCollection !== null) {
        this.viewer.scene.primitives.remove(this.labelCollection);
      }
      if (this.pointCollection !== null) {
        this.viewer.scene.primitives.remove(this.pointCollection);
      }
      if (this.ringCollection !== null) {
        this.viewer.scene.primitives.remove(this.ringCollection);
      }
    }
    this.labelCollection = null;
    this.pointCollection = null;
    this.ringCollection = null;
    this.viewer = null;
  }

  /** True iff currently attached. */
  isAttached(): boolean {
    return this.viewer !== null;
  }

  // ─── Internals ───────────────────────────────────────────────────────

  private cardinalWorldPosition(
    enuFrame: Cesium.Matrix4,
    dir: CardinalDirection,
  ): Cesium.Cartesian3 {
    const offset = cardinalEnuOffset(dir, HORIZON_LABEL_OFFSET_M);
    const enu = new Cesium.Cartesian3(offset.east, offset.north, offset.up);
    return Cesium.Matrix4.multiplyByPoint(enuFrame, enu, new Cesium.Cartesian3());
  }
}
